import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PortalSidebar from "../../admin-shared/PortalSidebar";
import { NATURALS_FLOW_CURRENT_VERSION } from "../../admin-shared/naturalsFlowReleaseNotes";
import { buildOverviewFirstSidebarItems } from "../../admin-shared/overviewSidebarItems";
import {
  buildWorkspaceEngagementSidebarItems,
  isWorkspaceEngagementPath,
} from "../../engagement/layout/partnerEngagementSidebarItems";
import { buildWorkspaceWhatsAppSidebarItems, isWorkspaceWhatsAppPath } from "../../whatsapp/sidebarItems";
import useEngagementCounts from "../../engagement/hooks/useEngagementCounts";
import { permissionService } from "../services/permissionService";
import { useOrgEntitlementsRevision } from "../../admin-shared/syncOrgEntitlements";
import nyafaz from "../assets/nyafazlogo.png";
import { useUser } from "../context/UserContext";
import { loginPathForRole } from "../utils/loginPaths";
import { api, clearPortalStorage, PORTAL_ID } from "../utils/api";
import {
  ORG_LOGO_UPDATED_EVENT,
  ENTERPRISE_DEFAULT_ORG_LOGO,
  resolveMediaUrl,
  resolvePartnerLogoSrc,
  resolveStoredOrgLogo,
} from "../utils/mediaUrl";
import { fetchOrganizationLogo } from "../utils/orgLogoLoader";

const Sidebar = ({ expanded, setExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useUser() || {};
  const isFranchise = permissionService.isFranchise();
  const pickLogo = (orgLogo) =>
    resolveMediaUrl(orgLogo) || resolveStoredOrgLogo() || resolvePartnerLogoSrc(orgLogo) || nyafaz;

  const [logoSrc, setLogoSrc] = useState(() => pickLogo(user?.org_logo));
  const [viewport, setViewport] = useState("desktop");

  useEffect(() => {
    setLogoSrc(pickLogo(user?.org_logo));
  }, [user?.org_logo, isFranchise]);

  useEffect(() => {
    const onLogoUpdated = (event) => {
      setLogoSrc(pickLogo(event?.detail?.url));
    };
    window.addEventListener(ORG_LOGO_UPDATED_EVENT, onLogoUpdated);
    return () => window.removeEventListener(ORG_LOGO_UPDATED_EVENT, onLogoUpdated);
  }, [isFranchise]);

  useEffect(() => {
    if (!localStorage.getItem("naturals_workspace_userId")) return undefined;

    let cancelled = false;
    (async () => {
      const logo = await fetchOrganizationLogo({ isFranchise });
      if (cancelled || !logo) return;
      const display = resolveMediaUrl(logo) || logo;
      setLogoSrc(display);
      if (typeof setUser === "function") {
        setUser((prev) => (prev ? { ...prev, org_logo: display } : prev));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setUser, isFranchise]);

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      if (width < 768) setViewport("mobile");
      else if (width < 1367) setViewport("tablet");
      else setViewport("desktop");
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const handleLogout = () => {
    const userId = localStorage.getItem(`naturals_${PORTAL_ID}_userId`);
    api(`/${PORTAL_ID}/logout`, {
      method: "POST",
      body: JSON.stringify({ userId, audience: PORTAL_ID }),
    }).catch(() => {});
    clearPortalStorage();
    navigate(loginPathForRole());
  };

  const onEngagement = isWorkspaceEngagementPath(location.pathname);
  const onWhatsApp = isWorkspaceWhatsAppPath(location.pathname);
  const engagementCounts = useEngagementCounts(onEngagement);
  const entRev = useOrgEntitlementsRevision();

  const menuItems = useMemo(() => {
    if (onEngagement) {
      return buildWorkspaceEngagementSidebarItems(location.pathname, engagementCounts);
    }
    if (onWhatsApp) {
      return buildWorkspaceWhatsAppSidebarItems();
    }
    return buildOverviewFirstSidebarItems("workspace", location.pathname, permissionService);
  }, [location.pathname, onEngagement, onWhatsApp, engagementCounts, entRev]);

  const isCompactViewport = viewport === "tablet" || viewport === "mobile";

  return (
    <PortalSidebar
      menuItems={menuItems}
      logoSrc={logoSrc}
      logoFallback={isFranchise ? ENTERPRISE_DEFAULT_ORG_LOGO : nyafaz}
      isFranchise={isFranchise}
      expanded={expanded}
      setExpanded={setExpanded}
      onLogout={handleLogout}
      isCompactViewport={isCompactViewport}
      locationPathname={location.pathname}
      releaseNotesPath="/nyafaz-ep/workspace/naturals-flow-release"
      versionLabel={NATURALS_FLOW_CURRENT_VERSION}
    />
  );
};

export default Sidebar;
